import { useState, useEffect, useMemo, type CSSProperties } from 'react';
import {
  collection, query, where, getDocs, doc, runTransaction, serverTimestamp,
} from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../hooks/useSettings';
import { Button, Modal, ModalBanner, FormField } from './ui';
import { fmtMoney } from '../utils/currency';
import {
  buildEntry, postEntry, entryId, linesPurchaseReturn,
} from '../utils/ledger';
import { returnGst, returnCreditOf } from '../utils/tradingSystem';
import type { PurchaseInvoice, InventoryItem, PaymentStatus } from '../types';

function todayISO() { return new Date().toISOString().slice(0, 10); }
function stripUndef<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}
const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

// Sales-return-style Excel grid — borderless cells, sticky header, inputs fill the cell.
const GTD: CSSProperties = { padding: 0, verticalAlign: 'middle' };
const GTD_TEXT: CSSProperties = { ...GTD, padding: '6px 8px' };
const GTH: CSSProperties = {
  position: 'sticky', top: 0, zIndex: 2, background: 'var(--surface-2)',
  padding: '6px 8px', fontSize: 9.5, fontWeight: 700, color: 'var(--text-3)',
  textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left',
  whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)',
};
// Full invoice No is stored; only prefix + last 4 digits show on screen.
function lotNoDisplay(invoiceNo: string) {
  if (!invoiceNo) return '—';
  const m = invoiceNo.match(/^([A-Za-z]+)-\d+-(\d+)$/);
  return m ? `${m[1]}-${m[2].slice(-4)}` : invoiceNo.slice(-4);
}
const codePillStyle: CSSProperties = {
  display: 'inline-block', fontFamily: 'var(--font-num)', background: 'var(--primary-light)', color: 'var(--primary)',
  padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700,
  letterSpacing: '0.02em',
};
function normalizeCode(code: string): string {
  return code.trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '_').slice(0, 400);
}

interface LineState { returnCarat: string; reason: string }

export default function PurchaseReturnModal({ invoice, supplierName, onClose }: {
  invoice: PurchaseInvoice;
  supplierName: string;
  onClose: () => void;
}) {
  const { user, business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);

  const [items, setItems]   = useState<InventoryItem[] | null>(null);
  const [date, setDate]     = useState(todayISO());
  const [notes, setNotes]   = useState('');
  const [ls, setLs]         = useState<Record<string, LineState>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  useEffect(() => {
    const db = getClientDb();
    if (!db) return;
    getDocs(query(collection(db, 'inventory'), where('purchaseId', '==', invoice.id)))
      .then(snap => {
        const rows = snap.docs
          .map(d => ({ id: d.id, ...d.data() } as InventoryItem))
          .filter(i => i.status === 'in-stock' && (i.carat ?? 0) > 1e-6);
        setItems(rows);
      })
      .catch(() => setItems([]));
  }, [invoice.id]);

  // Carats out on memo with a customer aren't here to send back: only the
  // free part of a stone can be returned to the supplier.
  const onMemo = (it: InventoryItem) => Math.max(0, it.memoCommittedCarat ?? 0);
  const freeCt = (it: InventoryItem) => Math.max(0, round2(it.carat - onMemo(it)));

  const set = (id: string, patch: Partial<LineState>) =>
    setLs(s => ({ ...s, [id]: { ...(s[id] ?? { returnCarat: '', reason: '' }), ...patch } }));

  const planned = useMemo(() => {
    if (!items) return [];
    return items
      .map(it => {
        const rc = parseFloat(ls[it.id]?.returnCarat ?? '');
        if (isNaN(rc) || rc <= 0) return null;
        const costOfReturn = round2((it.costUSD ?? 0) * (Math.min(rc, it.carat) / it.carat));
        return { it, carat: rc, costUSD: costOfReturn, reason: ls[it.id]?.reason?.trim() || undefined };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null);
  }, [items, ls]);

  const totalCarat = round2(planned.reduce((s, p) => s + p.carat, 0));
  const totalCost  = round2(planned.reduce((s, p) => s + p.costUSD, 0));
  // Indian GST on the goods going back, at the invoice's own rates.
  const totalGst   = returnGst(invoice, totalCost)?.taxTotalUSD ?? 0;

  async function save() {
    if (planned.length === 0) return setError('Enter a carat to return on at least one line.');
    const over = planned.find(p => p.carat > freeCt(p.it) + 1e-6);
    if (over) {
      const m = onMemo(over.it);
      return setError(`${over.it.code || over.it.shape}: only ${freeCt(over.it).toFixed(2)} ct can go back${m > 1e-6 ? ` (${m.toFixed(2)} ct is out on memo)` : ''}.`);
    }
    setSaving(true); setError('');
    const db = getClientDb();
    if (!db || !business) { setError('Not connected.'); setSaving(false); return; }

    const prefix = (settings.purchaseReturnPrefix || 'PR').toUpperCase();
    const retRef = doc(collection(db, 'purchaseReturns'));
    const invRef = doc(db, 'invoices', invoice.id);
    const counterRef = doc(db, 'counters', business.id);

    try {
      await runTransaction(db, async tx => {
        // ── Reads ──
        const counterSnap = await tx.get(counterRef);
        const n = (counterSnap.exists() ? (counterSnap.data().purchaseReturn ?? 0) : 0) + 1;
        const fresh = await Promise.all(planned.map(p => tx.get(doc(db, 'inventory', p.it.id))));
        const invSnap = await tx.get(invRef);
        if (!invSnap.exists()) throw new Error('That purchase invoice no longer exists.');

        const rows = planned.map((p, i) => {
          const s = fresh[i];
          if (!s.exists()) throw new Error(`${p.it.shape} is no longer in inventory.`);
          const cur = { id: s.id, ...s.data() } as InventoryItem;
          if (cur.status !== 'in-stock') throw new Error(`${p.it.shape} is already ${cur.status} — can't return it.`);
          const curMemo = Math.max(0, cur.memoCommittedCarat ?? 0);
          const curFree = round2(cur.carat - curMemo);
          if (p.carat > curFree + 1e-6) {
            throw new Error(`Only ${Math.max(0, curFree).toFixed(2)} ct of ${p.it.code || p.it.shape} can go back${curMemo > 1e-6 ? ` (${curMemo.toFixed(2)} ct is out on memo)` : ''}.`);
          }
          const costOfReturn = round2((cur.costUSD ?? 0) * (p.carat / cur.carat));
          const costingOfReturn = round2((cur.costingUSD ?? 0) * (p.carat / cur.carat));
          const wholeItem = round2(cur.carat - p.carat) <= 1e-6;
          return { p, cur, costOfReturn, costingOfReturn, wholeItem };
        });

        const totCost = round2(rows.reduce((s, r) => s + r.costOfReturn, 0));
        const totCt   = round2(rows.reduce((s, r) => s + r.p.carat, 0));
        const inv = invSnap.data() as PurchaseInvoice;
        // GST on the returned goods comes off what we owe too (the invoice
        // total includes it), at the rates the invoice charged.
        const gstF = returnGst(inv, totCost);
        const credit = returnCreditOf(totCost, gstF ?? {});
        const newCredited = round2((inv.creditedUSD ?? 0) + credit);
        const settled = (inv.paidUSD ?? 0) + newCredited;
        const newStatus: PaymentStatus =
          settled >= inv.totalAmountUSD - 0.005 ? 'paid' : settled > 0 ? 'partial' : 'unpaid';

        // ── Writes ──
        tx.set(counterRef, { purchaseReturn: n }, { merge: true });
        tx.set(retRef, stripUndef({
          businessId: business.id,
          returnNo: `${prefix}-${new Date().getFullYear()}-${String(n).padStart(4, '0')}`,
          invoiceId: invoice.id, invoiceNo: invoice.invoiceNo,
          fromEntityId: invoice.fromEntityId, date,
          lines: rows.map(r => stripUndef({
            inventoryItemId: r.cur.id ?? r.p.it.id, lineItemId: r.cur.lineItemId,
            code: r.cur.code || undefined, shape: r.cur.shape,
            carat: r.p.carat, costUSD: r.costOfReturn, costingUSD: r.costingOfReturn,
            wholeItem: r.wholeItem, reason: r.p.reason,
          })),
          totalCarat: totCt, totalCostUSD: totCost,
          ...(gstF ?? {}),
          notes: notes.trim() || undefined,
          createdAt: serverTimestamp(), createdBy: user!.id,
        }));

        rows.forEach(r => {
          const itRef = doc(db, 'inventory', r.cur.id ?? r.p.it.id);
          const newCarat = round2(r.cur.carat - r.p.carat);
          if (r.wholeItem) {
            tx.update(itRef, stripUndef({
              carat: 0, costUSD: 0, costingUSD: 0,
              status: 'returned', returnedDate: date, returnReason: r.p.reason,
              updatedAt: serverTimestamp(),
            }));
          } else {
            tx.update(itRef, stripUndef({
              carat: newCarat,
              costUSD: round2((r.cur.costUSD ?? 0) - r.costOfReturn),
              costingUSD: round2((r.cur.costingUSD ?? 0) - r.costingOfReturn),
              updatedAt: serverTimestamp(),
            }));
          }
          if (r.cur.code) {
            tx.set(doc(collection(db, 'itemCodes', normalizeCode(r.cur.code), 'history')), stripUndef({
              type: 'return-out', date, carat: r.p.carat,
              entityId: invoice.fromEntityId, amountUSD: r.costOfReturn,
              inventoryItemId: r.cur.id ?? r.p.it.id,
              notes: `Returned to supplier (${invoice.invoiceNo})`,
              returnId: retRef.id,   // so deleting the return removes this line too
              createdAt: serverTimestamp(), createdBy: user!.id,
            }));
          }
        });

        tx.update(invRef, { creditedUSD: newCredited, paymentStatus: newStatus, updatedAt: serverTimestamp() });

        postEntry(tx, db, buildEntry({
          businessId: business.id, sourceType: 'purchase_return', sourceId: retRef.id,
          sourceCollection: 'purchaseReturns', date, currency: settings.currency,
          createdBy: user!.id, note: `Return against ${invoice.invoiceNo}`,
          lines: linesPurchaseReturn(totCost, invoice.fromEntityId, gstF?.taxTotalUSD ?? 0),
        }), entryId('purchase_return', retRef.id));
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not record the return.');
    }
    setSaving(false);
  }

  return (
    <Modal
      open onClose={onClose} size="2xl" hideHead
      footer={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
          <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
            Returning <strong style={{ color: 'var(--text-1)', fontFamily: 'var(--font-num)' }}>{totalCarat.toFixed(2)} ct</strong>
            {' · '}credit <strong style={{ color: 'var(--text-1)', fontFamily: 'var(--font-num)' }}>{money(round2(totalCost + totalGst))}</strong>
            {totalGst > 0 && <> (incl. GST {money(totalGst)})</>}
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={save} loading={saving} disabled={planned.length === 0}>Save</Button>
          </div>
        </div>
      }
    >
      <ModalBanner title={supplierName} eyebrow={`Purchase Return · ${lotNoDisplay(invoice.invoiceNo)}`} onClose={onClose} />

      {error && <div className="error-msg" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="form-row-2" style={{ marginBottom: 14 }}>
        <FormField label="Return date">
          <input type="date" value={date} max={todayISO()} onChange={e => setDate(e.target.value)} />
        </FormField>
        <FormField label="Notes (optional)">
          <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Why is this going back?" />
        </FormField>
      </div>

      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
        Items to send back
      </div>

      {items === null ? (
        <div className="skeleton" style={{ height: 160, borderRadius: 8 }} />
      ) : items.length === 0 ? (
        <div style={{ padding: '28px 16px', textAlign: 'center', fontSize: 13, color: 'var(--text-3)', border: '1px dashed var(--border)', borderRadius: 'var(--r-sm)' }}>
          Nothing from this invoice is still in stock — its items have been sold, merged, or already returned.
        </div>
      ) : (
        <div style={{
          border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)',
          overflow: 'auto', maxHeight: 360, overscrollBehavior: 'contain',
        }}>
          <table className="excel-grid-table">
            <thead>
              <tr>
                <th style={{ ...GTH, minWidth: 90 }}>Code</th>
                <th style={{ ...GTH, minWidth: 100 }}>SKU</th>
                <th style={{ ...GTH, minWidth: 90 }}>Shape</th>
                <th style={{ ...GTH, minWidth: 70 }}>Color</th>
                <th style={{ ...GTH, minWidth: 80 }}>Clarity</th>
                <th style={{ ...GTH, textAlign: 'right', width: 100 }}>In Stock ct</th>
                <th style={{ ...GTH, textAlign: 'right', width: 90 }} title="Out with a customer on memo. Can't go back until it's returned.">On Memo</th>
                <th style={{ ...GTH, width: 130 }}>Return ct</th>
                <th style={{ ...GTH, textAlign: 'right', width: 120 }}>Price/ct</th>
                <th style={{ ...GTH, textAlign: 'right', width: 130 }}>Credit</th>
                <th style={{ ...GTH, minWidth: 200 }}>Return Reason</th>
              </tr>
            </thead>
            <tbody>
              {items.map(it => {
                const rc = parseFloat(ls[it.id]?.returnCarat ?? '');
                const pricePerCt = it.carat > 0 ? (it.costUSD ?? 0) / it.carat : 0;
                const credit = !isNaN(rc) && rc > 0
                  ? round2((it.costUSD ?? 0) * (Math.min(rc, it.carat) / it.carat))
                  : 0;
                const memoCt = onMemo(it);
                const free = freeCt(it);
                const tooMuch = !isNaN(rc) && rc > free + 1e-6;
                return (
                  <tr key={it.id}>
                    <td style={{ ...GTD_TEXT, fontSize: 12 }}>
                      {it.code ? <span style={codePillStyle}>{it.code}</span> : <span style={{ opacity: 0.4 }}>—</span>}
                    </td>
                    <td style={{ ...GTD_TEXT, fontSize: 12, fontFamily: 'var(--font-num)', color: 'var(--text-2)' }}>{it.sku || <span style={{ opacity: 0.4 }}>—</span>}</td>
                    <td style={{ ...GTD_TEXT, fontSize: 12, color: 'var(--text-2)' }}>{it.shape || '—'}</td>
                    <td style={{ ...GTD_TEXT, fontSize: 12, color: 'var(--text-2)' }}>{it.color || '—'}</td>
                    <td style={{ ...GTD_TEXT, fontSize: 12, color: 'var(--text-2)' }}>{it.clarity || '—'}</td>
                    <td style={{ ...GTD_TEXT, textAlign: 'right', fontFamily: 'var(--font-num)', fontSize: 12, color: 'var(--text-2)' }}>{it.carat.toFixed(2)}</td>
                    <td style={{ ...GTD_TEXT, textAlign: 'right', fontFamily: 'var(--font-num)', fontSize: 12, color: memoCt > 1e-6 ? 'var(--warning)' : 'var(--text-3)' }}>
                      {memoCt > 1e-6 ? memoCt.toFixed(2) : <span style={{ opacity: 0.4 }}>—</span>}
                    </td>
                    <td style={{ ...GTD, background: tooMuch ? 'color-mix(in srgb, var(--danger) 12%, transparent)' : undefined }}
                      title={free <= 1e-6 ? 'All of it is out on memo' : `Up to ${free.toFixed(2)} ct`}>
                      <input type="number" step="0.01" min="0" max={free} className="grid-cell no-spinner"
                        value={ls[it.id]?.returnCarat ?? ''}
                        onChange={e => set(it.id, { returnCarat: e.target.value })}
                        disabled={free <= 1e-6}
                        placeholder={free <= 1e-6 ? 'On memo' : `max ${free.toFixed(2)}`}
                        style={{ textAlign: 'right', fontFamily: 'var(--font-num)' }} />
                    </td>
                    <td style={{ ...GTD_TEXT, textAlign: 'right', fontFamily: 'var(--font-num)', fontSize: 12, color: 'var(--text-2)' }}>{money(pricePerCt)}</td>
                    <td style={{ ...GTD_TEXT, textAlign: 'right', fontFamily: 'var(--font-num)', fontSize: 12, color: credit ? 'var(--text-1)' : 'var(--text-3)', fontWeight: credit ? 600 : 400 }}>
                      {credit ? money(credit) : '—'}
                    </td>
                    <td style={GTD}>
                      <input value={ls[it.id]?.reason ?? ''} onChange={e => set(it.id, { reason: e.target.value })}
                        placeholder="Optional reason" className="grid-cell" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
